import {
  Box,
  Grid,
  Slide,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useGetInvoiceQuery } from "api/api-invoices";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { DEFAULT_ROWS } from "codes";
import DataTableOldv2 from "components/Common/DataTablev2/DataTableOldv2";
import Layout from "components/Common/Layout";
import SkeletonLoad from "components/Common/SkeletonLoad";
import { currencyFormatter } from "helpers/currencyFormatter";
import useWidth from "helpers/useWidth";
import useIsMobile from "hooks/useIsMobile";
import React, { useEffect, useRef, useState } from "react";
import ActiveInvoicesVisualization from "./ActiveInvoicesVisualization";
import { useAppSelector } from "redux/hooks";

const ActiveInvoices = () => {
  const isMobile = useIsMobile();
  const USER_PERMISSION = useAppSelector(
    (state) => state.appState.user_permission
  );
  const { data: curr_user } = useGetCurrentUserQuery();
  const id = curr_user?.id;
  const {
    data: accounts,
    isFetching,
    isLoading,
    refetch,
  } = useGetInvoiceQuery(
    {
      endpoint: `dashboard/investors-summary?active_invoices=true`,
    },
    {
      // Force refetch on component mount and when refreshTrigger changes
      refetchOnMountOrArgChange: true,
      skip: !id, // Skip if id is not available
    }
  );
  const textSx = {
    fontSize: "1.1em!important",
    lineHeight: "1.5em",
    overflow: "hidden",
    maxWidth: "100%",
    textOverflow: "ellipsis",
    whiteSpace: "normal",
    wordWrap: "break-word",
  };
  const settings = curr_user?.settings ?? {};
  const rowsPerPage = settings?.rowsPerPage ?? DEFAULT_ROWS;
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  const ref = useRef<HTMLDivElement>(null);
  const width = ref ? useWidth(ref) : 1000;
  const headerLength = Object.keys(accounts?.invoices?.[0] ?? {})?.length;
  const defaultColumnWidth = width ? (width / headerLength) * 1.6 : 200;
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>("in_progress");
  const [tableKey, setTableKey] = useState<number>(0); // Key to force table re-render

  const handleFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newStatus: string | null
  ) => {
    setFilterStatus(newStatus);
    // Force table to re-render and reset pagination
    setTableKey((prev) => prev + 1);
  };

  // Reset table when filter changes
  useEffect(() => {
    setTableKey((prev) => prev + 1);
  }, [filterStatus]);

  const filteredInvoices = accounts?.invoices?.filter((invoice: any) => {
    // First filter out pre-listing invoices
    if (invoice.phase === "0_PRE_LISTING" || 
        invoice.phase === "PRE_LISTING" || 
        invoice.status === "pre_listing" ||
        invoice.status === "prelisting") {
      return false;
    }

    // Then filter by status
    if (filterStatus === "in_progress") {
      return invoice.phase === "3_IN_PROGRESS";
    } else if (filterStatus === "closed") {
      return invoice.phase === "4_CLOSED";
    }
    // Default to in_progress if no filter is selected
    return invoice.phase === "3_IN_PROGRESS";
  });

  // Transform the data to only include required columns in the correct order
  const transformedInvoices = filteredInvoices?.map((invoice: any) => ({
    invoice_id: invoice.invoice_id,
    buyer_name: invoice.buyer_name,
    importer_country: invoice.importer_country,
    supplier_name: invoice.supplier_name,
    exporter_country: invoice.exporter_country,
    sector: invoice.sector,
    finance_date: invoice.finance_date,
    invoice_created_date: invoice.invoice_created_date,
    principle_amount: invoice.principle_amount,
    currency: invoice.currency || "USD",
    phase: invoice.phase || "3_IN_PROGRESS",
    expected_repayment_date: invoice.expected_repayment_date,
  })) || [];

  // Debug: Log the first invoice to check data structure
  useEffect(() => {
    if (transformedInvoices.length > 0) {
      console.log("First transformed invoice:", transformedInvoices[0]);
      console.log("Original first invoice:", filteredInvoices?.[0]);
    }
  }, [transformedInvoices, filteredInvoices]);

  // Helper function to format status values
  const formatStatus = (status: string) => {
    if (status === "3_IN_PROGRESS") return "In Progress";
    if (status === "4_CLOSED") return "Closed";
    return status;
  };

  const customColumns = {
    invoice_id: {
      headerName: "Invoice ID",
      type: "text",
      minWidth: 90,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{params.value || "--"}</Typography>
      ),
    },
    buyer_name: {
      headerName: "Buyer Name",
      type: "text",
      minWidth: 110,
      renderCell: (params: any) => (
        <Typography
          sx={{
            ...textSx,
            cursor: "pointer",
            color: "primary.main",
            textDecoration: "underline",
          }}
          onClick={() => {
            const allInvoices = accounts?.invoices ?? [];
            const matchedInvoices = allInvoices.filter(
              (inv: any) => inv.buyer_name === params.value
            );
            setSelectedRow(matchedInvoices);
          }}
        >
          {params.value || "--"}
        </Typography>
      ),
    },
    importer_country: {
      headerName: "Buyer Country",
      type: "text",
      minWidth: 80,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{params.value || "--"}</Typography>
      ),
    },
    supplier_name: {
      headerName: "Supplier Name",
      type: "text",
      minWidth: 110,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{params.value || "--"}</Typography>
      ),
    },
    exporter_country: {
      headerName: "Supplier Country",
      type: "text",
      minWidth: 80,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{params.value || "--"}</Typography>
      ),
    },
    sector: {
      headerName: "Sector",
      type: "text",
      minWidth: 90,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{params.value || "--"}</Typography>
      ),
    },
    finance_date: {
      headerName: "Finance Date",
      type: "date",
      minWidth: 110,
      valueFormatter: formatDate,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{formatDate(params?.value)}</Typography>
      ),
    },
    invoice_created_date: {
      headerName: "Invoice Created At",
      type: "text",
      minWidth: 160,
      valueFormatter: formatDate,
      renderCell: (params: any) => (
        <Typography sx={textSx}>
          {formatDate(params?.value)}
        </Typography>
      ),
    },
    principle_amount: {
      headerName: "Amount Invested",
      type: "number",
      minWidth: 100,
      renderCell: (params: any) => (
        <Typography sx={textSx}>
          {currencyFormatter({
            amount: Number(params.value),
          })}
        </Typography>
      ),
    },
    currency: {
      headerName: "CCY",
      type: "text",
      minWidth: 60,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{params.value || "--"}</Typography>
      ),
    },
    phase: {
      headerName: "Status",
      type: "text",
      minWidth: 90,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{formatStatus(params.value) || "--"}</Typography>
      ),
    },
    expected_repayment_date: {
      headerName: "Expected Repayment Date",
      type: "text",
      minWidth: 140,
      valueFormatter: formatDate,
      renderCell: (params: any) => (
        <Typography sx={textSx}>{formatDate(params?.value)}</Typography>
      ),
    },
  };

  const toBeHiddenFields = [
    "total_amount", 
    "interest_amount",
    "days_financed",
    "invoice_due_date",
    "name",
    "status",
    "due_date",
    "year",
    "funder_discount_rate"
  ];

  if (!USER_PERMISSION?.includes("show_master_investor")) {
    toBeHiddenFields.push("funder_name");
  }

  useEffect(() => {}, [isFetching]);
  const initialFilters = {
    status: {
      comparator: "is not",
      keywords: [],
    },
  };

  // Modal state for row click

  return (
    <Slide in direction="left">
      <Box sx={{ display: "flex", flexDirection: "column" }} flexGrow={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h3" fontWeight="bold">
              Active Invoices
            </Typography>
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={handleFilterChange}
              aria-label="invoice filter"
            >
              <ToggleButton value="in_progress" aria-label="in progress">
                In Progress
              </ToggleButton>
              <ToggleButton value="closed" aria-label="closed">
                R/paid/Closed in Current Year
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Layout
          
        >
          <Grid item xs={12} margin="5px">
            <Grid item xs={12} ref={ref}>
              {(isFetching || isLoading) && <SkeletonLoad bars={10} />}
              
              {/* No Data Message */}
              {!isFetching &&
                !isLoading &&
                transformedInvoices !== undefined &&
                transformedInvoices.length === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "300px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      padding: "40px",
                      textAlign: "center",
                      border: "2px dashed #e0e0e0"
                    }}
                  >
                    📋
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mt: 2, 
                        mb: 1, 
                        fontWeight: "600",
                        color: "#666"
                      }}
                    >
                      No Active Invoices
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: "#888",
                        maxWidth: "400px"
                      }}
                    >
                      {filterStatus === "in_progress" 
                        ? "No invoices are currently in progress. Try viewing closed invoices."
                        : "No closed invoices found for the current year."
                      }
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <ToggleButtonGroup
                        value={filterStatus}
                        exclusive
                        onChange={handleFilterChange}
                        aria-label="invoice filter"
                        size="small"
                      >
                        <ToggleButton value="in_progress" aria-label="in progress">
                          In Progress
                        </ToggleButton>
                        <ToggleButton value="closed" aria-label="closed">
                          R/paid/Closed in Current Year
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                )}

              {/* Data Table */}
              {!isFetching &&
                transformedInvoices !== undefined &&
                Boolean(transformedInvoices?.length) && (
                  <DataTableOldv2
                    key={tableKey}
                    data={transformedInvoices}
                    initialFilters={initialFilters}
                    defaultColumnWidth={defaultColumnWidth}
                    rowsPerPage={rowsPerPage}
                    useKey="invoice_id"
                    customColumns={customColumns}
                    toBeHidden={toBeHiddenFields}
                    toolBar={false}
                    sx={{
                      width: "100%",
                      "& .MuiTableContainer-root": {
                        overflowX: "auto",
                      },
                      table: {
                        minWidth: "max-content",
                      },
                    }}
                  />
                )}
            </Grid>
          </Grid>
          {/* Modal for row details */}
          <Dialog
            open={!!selectedRow}
            onClose={() => setSelectedRow(null)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: "1.25rem" }}>
              Invoice Details
            </DialogTitle>
            <DialogContent dividers>
              <ActiveInvoicesVisualization importerData={selectedRow} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedRow(null)} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Layout>
      </Box>
    </Slide>
  );
};

export default ActiveInvoices;
